import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function Card({ title, subtitle, actions, children, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p className="muted">{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({ label, error, hint, children, required }) {
  return (
    <label className={`field ${error ? 'field-error' : ''}`}>
      <span className="field-label">
        {label}
        {required && <em aria-hidden="true"> *</em>}
      </span>
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-message">{error}</span>}
    </label>
  );
}

export function Input({ ...props }) {
  return <input {...props} />;
}

export function Alert({ kind = 'info', children, onDismiss }) {
  if (!children) return null;
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span>{children}</span>
      {onDismiss && (
        <button type="button" className="alert-close" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

const STATUS_TONES = {
  draft: 'neutral',
  pending_review: 'warning',
  more_info_required: 'warning',
  approved: 'success',
  rejected: 'danger',
  available: 'success',
  assigned: 'info',
  inactive: 'neutral',
  requested: 'warning',
  confirmed: 'success',
  completed: 'neutral',
  cancelled: 'danger',
  pending: 'warning',
};

export function StatusPill({ status, label }) {
  const { t } = useTranslation();
  return (
    <span className={`pill pill-${STATUS_TONES[status] || 'neutral'}`}>
      {t(`status.${status}`, { defaultValue: label || status })}
    </span>
  );
}

export function Button({ variant = 'primary', loading, children, ...props }) {
  return (
    <button className={`btn btn-${variant}`} disabled={loading || props.disabled} {...props}>
      {loading ? 'Please wait…' : children}
    </button>
  );
}

export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="alert-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function StarRating({ value = 0, onChange, readOnly = false }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className={`star-rating${readOnly ? ' star-rating-readonly' : ''}`}>
      {stars.map((n) => (
        <span
          key={n}
          className={`star${n <= Math.round(value) ? ' filled' : ''}`}
          onClick={readOnly ? undefined : () => onChange(n)}
          role={readOnly ? undefined : 'button'}
          tabIndex={readOnly ? undefined : 0}
          aria-label={readOnly ? undefined : `Rate ${n} star${n > 1 ? 's' : ''}`}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(n);
                  }
                }
          }
        >
          ★
        </span>
      ))}
    </div>
  );
}

export function EmptyState({ title, children }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p className="muted">{children}</p>}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return <div className="spinner">{label}</div>;
}

/** Transient success message that clears itself. */
export function useFlash(timeout = 4000) {
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (!message) return undefined;
    const id = setTimeout(() => setMessage(''), timeout);
    return () => clearTimeout(id);
  }, [message, timeout]);
  return [message, setMessage];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDate(iso, { long = false } = {}) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const month = long
    ? ['January','February','March','April','May','June','July','August','September','October','November','December'][m - 1]
    : MONTHS[m - 1];
  return `${month} ${d}, ${y}`;
}

export function formatTime(hhmm) {
  if (!hhmm || !/^\d{2}:\d{2}/.test(hhmm)) return hhmm || '—';
  const [h, m] = hhmm.slice(0, 5).split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  const date = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function formatSlot(booking) {
  return `${formatDate(booking.bookingDate)} · ${formatTime(booking.startTime)} – ${formatTime(booking.endTime)}`;
}
